import { Controller } from "@hotwired/stimulus";

// The subscribe flyover in the status page header. This is a form rather than a
// menu of links, so it doesn't use the shared dropdown controller: opening it
// should put the cursor in the email field, and Escape should close it and hand
// focus back to the toggle.
//
// Where the captcha is required it also gates submission. Without that the
// button stays live when the widget errors out, and the post is rejected
// server-side after the person has already been told to check their email. The
// widget itself belongs to the shared `turnstile` controller; this one only
// reacts to the events it emits, wired up in `_subscribe.html.erb`.
export default class extends Controller {
  static targets = ["button", "panel", "email", "submit", "captcha", "error"];

  connect() {
    if (this.captchaRequired) this.setSubmitDisabled(true);
  }

  toggle(event) {
    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden");
    this.buttonTarget.setAttribute("aria-expanded", "true");
    if (this.hasEmailTarget) this.emailTarget.focus();
  }

  close() {
    this.panelTarget.classList.add("hidden");
    this.buttonTarget.setAttribute("aria-expanded", "false");
  }

  closeOnOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) this.close();
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || !this.isOpen) return;
    this.close();
    this.buttonTarget.focus();
  }

  captchaSolved() {
    this.setSubmitDisabled(false);
    this.showError(null);
  }

  captchaErrored() {
    this.captchaFailed("Verification failed. Please try again.");
  }

  captchaExpired() {
    this.captchaFailed("Verification expired. Please try again.");
  }

  captchaTimedOut() {
    this.captchaFailed(
      "Verification timed out. Please complete the check again.",
    );
  }

  captchaLoadFailed() {
    this.captchaFailed(
      "The verification check couldn't load. Disable any content blockers for this page and reload.",
    );
  }

  captchaFailed(message) {
    this.setSubmitDisabled(true);
    this.showError(message);
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message || "";
    this.errorTarget.classList.toggle("hidden", !message);
  }

  // The captcha element is only rendered on the hosts our Turnstile sitekey
  // authorizes, so its presence is what says whether a token is expected.
  get captchaRequired() {
    return this.hasCaptchaTarget;
  }

  get isOpen() {
    return !this.panelTarget.classList.contains("hidden");
  }
}
